// A bounded age for one domain-backed read whose payload carries a field no revision domain
// tracks.
//
// A domain-backed resource is fresh until its domain moves (`api/resource.ts`), which is right
// for nearly everything. A credential's `last_used_at` is the exception: it moves on every key
// request and deliberately bumps no domain (docs/plans/claude-connector.md §2), so "Last used"
// on Account and in the user drawer would otherwise read the moment the list was first loaded
// for as long as the tab lives. This re-reads the list when the screen showing it mounts or the
// window regains focus, and only when what it holds is older than `maxAgeMs`, so the loader's
// fresh read is never fetched twice.

import { useEffect, useRef } from 'react';

interface Refreshable {
  /** When the data was last fetched (TanStack's `dataUpdatedAt`; 0 before the first). */
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
}

export function useRefreshWhenOlderThan(query: Refreshable, maxAgeMs: number): void {
  const latest = useRef(query);
  latest.current = query;
  useEffect(() => {
    const refreshIfOld = () => {
      const { dataUpdatedAt, refetch } = latest.current;
      if (dataUpdatedAt > 0 && Date.now() - dataUpdatedAt > maxAgeMs) void refetch();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshIfOld();
    };
    refreshIfOld();
    window.addEventListener('focus', refreshIfOld);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshIfOld);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [maxAgeMs]);
}
